import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { EmptyState } from '@/components/EmptyState';
import { KPICard } from '@/components/KPICard';
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Copy,
  ExternalLink,
  Eye,
  RefreshCw,
  Shield,
  X,
} from 'lucide-react';
import { formatDate, formatNumber, formatPercent } from '@/lib/locale';
import { apiGet, apiPatch, apiPost, lastNDays, qs } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';
import { toast } from '@/lib/toast';

type Domain = {
  id: string;
  fqdn: string;
  organizational_domain?: string;
  tenant_slug: string;
  tenant_name?: string;
  desired_policy?: 'none' | 'quarantine' | 'reject';
  current_policy?: 'none' | 'quarantine' | 'reject';
  monitored_since?: string;
  status?: 'good' | 'warn' | 'fail' | 'onboarding';
  pass_30d?: number;
  volume_30d?: number;
  sources_count?: number;
  last_report_at?: string;
};

type DnsSnapshot = {
  id: string;
  fetched_at?: string;
  spf_record?: string | null;
  dmarc_record?: string | null;
  dkim_selectors?: Record<string, string> | null;
  mx_records?: { priority: number; exchange: string }[] | null;
  changed_from_previous?: boolean | null;
};

// Matches backend SuggestedDmarcOut (txt_record + instructions_nl + the
// RFC 7489 §7.1 external-destination authorization record on the ingest zone).
type Suggestion = {
  txt_record: string;
  txt_record_name?: string;
  instructions_nl?: string;
  report_authorization_name?: string;
  report_authorization_value?: string;
  report_authorization_nl?: string;
};

// Domain-scoped overview KPIs (analytics). The pass-rate fields are 0..1
// fractions; total_messages / unique_source_ips are integer counts.
type DomainOverview = {
  total_messages?: number;
  pass_rate?: number | null;
  spf_pass_rate?: number | null;
  dkim_pass_rate?: number | null;
  unique_source_ips?: number;
};

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

/** Pass-rate (0..1) → '—' on null/undefined, else nl-BE percent. */
function passText(frac?: number | null): string {
  return frac == null ? '—' : formatPercent(frac);
}

/** Accent colour for a pass-rate; undefined when the value is null. */
function passAccent(frac?: number | null): string | undefined {
  if (frac == null) return undefined;
  if (frac >= 0.95) return 'text-green-700';
  if (frac >= 0.9) return 'text-amber-700';
  return 'text-red-700';
}

/** Per-field copy-to-clipboard affordance; the whole field is the button. */
function CopyField({ value, ariaLabel }: { value: string; ariaLabel: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      void navigator.clipboard?.writeText(value);
    } catch {
      // clipboard unavailable; do nothing
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <>
      <button
        type="button"
        onClick={copy}
        aria-label={`${ariaLabel} kopiëren naar klembord`}
        className="group inline-flex max-w-full items-center gap-2 rounded bg-slate-100 px-2 py-1 text-left align-top font-mono text-slate-800 hover:bg-slate-200/70"
      >
        <span className="min-w-0 break-all">{value}</span>
        <span
          className={`flex-shrink-0 ${copied ? 'text-green-700' : 'text-slate-400 group-hover:text-info-700'}`}
          aria-hidden="true"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </span>
      </button>
      <span aria-live="polite" className="sr-only">
        {copied ? `${ariaLabel} gekopieerd` : ''}
      </span>
    </>
  );
}

// Uniform Naam/Type/Waarde rendering for a TXT DNS record (used for both the
// recommended DMARC record and the external-destination authorization record).
// Naam and Waarde are each their own per-field copy button.
function RecordFields({ name, value }: { name?: string; value: string }) {
  return (
    <dl className="space-y-1.5 text-[11.5px]">
      {name && (
        <div className="flex items-start gap-2">
          <dt className="w-14 flex-shrink-0 pt-1 text-slate-500">Naam</dt>
          <dd className="min-w-0 flex-1">
            <CopyField value={name} ariaLabel="Naam" />
          </dd>
        </div>
      )}
      <div className="flex items-start gap-2">
        <dt className="w-14 flex-shrink-0 pt-1 text-slate-500">Type</dt>
        <dd className="pt-1 font-mono text-slate-800">TXT</dd>
      </div>
      <div className="flex items-start gap-2">
        <dt className="w-14 flex-shrink-0 pt-1 text-slate-500">Waarde</dt>
        <dd className="min-w-0 flex-1">
          <CopyField value={value} ariaLabel="Waarde" />
        </dd>
      </div>
    </dl>
  );
}

type DnsHealthStatus = 'ok' | 'warn' | 'fail' | 'missing';

type DnsHealthCheck = {
  field: 'dmarc' | 'spf' | 'dkim' | 'mx';
  selector?: string | null;
  status: DnsHealthStatus;
  summary: string;
  detail_nl: string;
};

type DnsHealth = {
  fqdn: string;
  overall: 'ok' | 'warn' | 'fail';
  checks: DnsHealthCheck[];
};

// Matches backend AlignmentHelperOut (schemas/alignment.py): a per-domain
// object with actionable suggestions + per-sender signals. (The previous flat
// AlignmentEntry[] shape was fictional and crashed the page with `.map` on the
// returned object.)
type AlignmentSuggestion = {
  title: string;
  severity: 'info' | 'warn' | 'critical';
  rationale: string;
  actions: string[];
  docs_url?: string | null;
  senders?: { source_ip: string; label: string }[];
};

type SenderStatus =
  | 'authenticated'
  | 'forwarder'
  | 'insufficient_data'
  | 'suspicious'
  | 'unaligned';

type AlignmentSignal = {
  source_ip: string;
  asn_org?: string | null;
  reverse_dns?: string | null;
  esp_name?: string | null;
  count: number;
  spf_aligned_rate: number;
  dkim_aligned_rate: number;
  status?: SenderStatus;
};

const SENDER_STATUS_BADGE: Record<SenderStatus, { label: string; className: string }> = {
  authenticated: { label: 'Geauthenticeerd', className: 'bg-green-50 text-green-700' },
  forwarder: { label: 'Forwarder', className: 'bg-sky-50 text-sky-700' },
  insufficient_data: { label: 'Te weinig data', className: 'bg-slate-100 text-slate-600' },
  suspicious: { label: 'Verdacht', className: 'bg-red-50 text-red-700' },
  unaligned: { label: 'Niet aligned', className: 'bg-amber-50 text-amber-700' },
};

type AlignmentHelper = {
  signals: AlignmentSignal[];
  suggestions: AlignmentSuggestion[];
};

type TimelinePoint = {
  ts: string;
  pass: number;
  quarantine: number;
  reject: number;
};

type AggregateReport = {
  id: string;
  reporter_org?: string;
  received_at?: string;
  // Backend AggregateReportSummary uses date_range_begin/_end (not period_*).
  date_range_begin?: string;
  date_range_end?: string;
  records_count?: number;
};

type PolicyValue = 'none' | 'quarantine' | 'reject';

/** Parse the enforced policy (the ``p=`` tag) out of a published DMARC record. */
function policyFromRecord(record?: string | null): PolicyValue | undefined {
  if (!record) return undefined;
  for (const part of record.split(';')) {
    const [key, value] = part.split('=');
    if (key?.trim().toLowerCase() === 'p') {
      const p = value?.trim().toLowerCase();
      if (p === 'none' || p === 'quarantine' || p === 'reject') return p;
    }
  }
  return undefined;
}

export function DomainDetail({ slug, domainId }: { slug: string; domainId: string }) {
  const range = lastNDays(30);

  const domainQ = useQuery({
    queryKey: ['domain', slug, domainId],
    queryFn: () => apiGet<Domain>(`/tenants/${slug}/domains/${domainId}`, { tenant: slug }),
  });
  const dnsQ = useQuery({
    queryKey: ['domain', domainId, 'dns-snapshots', 'latest'],
    queryFn: () =>
      apiGet<DnsSnapshot[]>(`/domains/${domainId}/dns-snapshots${qs({ page: 1, per_page: 1 })}`, {
        tenant: slug,
      }),
  });
  const suggestQ = useQuery({
    queryKey: ['domain', domainId, 'suggested'],
    queryFn: () =>
      apiGet<Suggestion>(`/domains/${domainId}/dmarc-record-suggested`, {
        tenant: slug,
      }),
  });
  const healthQ = useQuery({
    queryKey: ['domain', domainId, 'dns-health'],
    queryFn: () => apiGet<DnsHealth>(`/domains/${domainId}/dns-health`, { tenant: slug }),
    // Live resolve hits public DNS — refetch once a minute is plenty.
    staleTime: 60_000,
  });
  const alignQ = useQuery({
    queryKey: ['alignment-helper', domainId],
    queryFn: () => apiGet<AlignmentHelper>(`/alignment-helper/${domainId}`, { tenant: slug }),
  });
  const timelineQ = useQuery({
    queryKey: ['timeline', 'domain', domainId, range.from, range.to],
    queryFn: () =>
      // Backend returns Envelope[TimelineOut] = { points: [...] } and the param
      // names are since/until + domain_id (not from/to/domain).
      apiGet<{ points: TimelinePoint[] }>(
        `/analytics/timeline${qs({
          tenant: slug,
          domain_id: domainId,
          granularity: 'day',
          since: range.from,
          until: range.to,
        })}`,
        { tenant: slug },
      ),
  });
  const overviewQ = useQuery({
    queryKey: ['analytics', 'overview', 'domain', domainId, range.from, range.to],
    queryFn: () =>
      apiGet<DomainOverview>(
        `/analytics/overview${qs({ tenant: slug, domain_id: domainId, since: range.from, until: range.to })}`,
        { tenant: slug },
      ),
  });
  const reportsQ = useQuery({
    queryKey: ['reports', 'domain', domainId, 'recent'],
    queryFn: () =>
      apiGet<AggregateReport[]>(
        `/reports/aggregate${qs({
          tenant: slug,
          domain_id: domainId,
          page: 1,
          per_page: 5,
        })}`,
        { tenant: slug },
      ),
  });

  const domain = domainQ.data;
  const dns = dnsQ.data?.[0];
  const suggest = suggestQ.data;
  const overview = overviewQ.data;
  const suggestions = alignQ.data?.suggestions ?? [];
  const signals = alignQ.data?.signals ?? [];
  const timeline = timelineQ.data?.points ?? [];
  const reports = reportsQ.data ?? [];

  const [showSenders, setShowSenders] = useState(false);

  // On-demand DNS check: resolve + persist a fresh snapshot now, then refresh
  // the snapshot, live health-check and suggestion.
  const qc = useQueryClient();
  const recheckDns = useMutation({
    mutationFn: () =>
      apiPost<{ changed?: boolean }>(`/domains/${domainId}/check-dns`, {}, { tenant: slug }),
    onSuccess: (res) => {
      toast({
        tone: 'success',
        message: res.changed ? 'DNS-snapshot bijgewerkt (gewijzigd).' : 'DNS-snapshot bijgewerkt.',
      });
      void qc.invalidateQueries({ queryKey: ['domain', domainId, 'dns-snapshots', 'latest'] });
      void healthQ.refetch();
      void suggestQ.refetch();
    },
    onError: () => toast({ tone: 'error', message: 'DNS-controle mislukt.' }),
  });

  const isLoading = domainQ.isLoading;
  const isError = domainQ.isError;
  const onRetry = () => {
    domainQ.refetch();
    dnsQ.refetch();
    suggestQ.refetch();
    healthQ.refetch();
    alignQ.refetch();
    timelineQ.refetch();
    overviewQ.refetch();
    reportsQ.refetch();
  };

  // Lifecycle phase + policy derivation.
  const isOnboarding = reports.length === 0;
  // Prefer the live health verdict; when it is still loading or the resolver
  // failed (overall undefined), fall back to the persisted domain status rather
  // than optimistically labelling an affected domain "Stabiel" (Codex P2).
  const status: 'good' | 'warn' | 'fail' | 'onboarding' = isOnboarding
    ? 'onboarding'
    : healthQ.data?.overall === 'fail'
      ? 'fail'
      : healthQ.data?.overall === 'warn'
        ? 'warn'
        : healthQ.data?.overall === 'ok'
          ? 'good'
          : (domain?.status ?? 'good');
  // current_policy reflects what is actually PUBLISHED. Prefer the freshly
  // resolved DMARC record (so the progression matches reality even before the
  // backend persists it on the next check), then the stored value.
  const currentPolicy = policyFromRecord(dns?.dmarc_record) ?? domain?.current_policy ?? 'none';
  const desiredPolicy = domain?.desired_policy ?? 'none';

  const setDesiredPolicy = useMutation({
    mutationFn: (policy: PolicyValue) =>
      apiPatch<unknown>(
        `/tenants/${slug}/domains/${domainId}`,
        { desired_policy: policy },
        { tenant: slug },
      ),
    onSuccess: () => {
      toast({ tone: 'success', message: 'Doelbeleid bijgewerkt.' });
      void qc.invalidateQueries({ queryKey: ['domain', slug, domainId] });
      void suggestQ.refetch();
    },
    onError: () => toast({ tone: 'error', message: 'Doelbeleid bijwerken mislukt.' }),
  });

  // Shared "Live DNS-status" card — rendered in both onboarding & monitoring
  // layouts (in the col-5 slot).
  const healthCard = (
    <Card>
      <CardHeader
        title="Live DNS-status"
        subtitle="live check op DMARC, SPF, DKIM en MX"
        right={
          <button
            type="button"
            onClick={() => healthQ.refetch()}
            disabled={healthQ.isFetching}
            className="inline-flex items-center gap-1.5 rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <RefreshCw size={11} className={healthQ.isFetching ? 'animate-spin' : undefined} />
            Opnieuw
          </button>
        }
      />
      {healthQ.isLoading ? (
        <div className="p-4 text-[12px] text-slate-500">Controle wordt uitgevoerd…</div>
      ) : healthQ.isError || !healthQ.data ? (
        <div className="p-4 text-[12px] text-red-600">
          Health-check kon niet worden uitgevoerd. Controleer of de DNS-resolver bereikbaar is.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 px-4 pb-2 pt-3 text-[12px]">
            <span className="text-slate-500">Algemeen oordeel:</span>
            <HealthOverallBadge overall={healthQ.data.overall} />
          </div>
          <div>
            {healthQ.data.checks.map((c, i) => (
              <HealthRow key={`${c.field}-${c.selector ?? ''}-${i}`} check={c} dns={dns} />
            ))}
          </div>
          <p className="px-4 py-2.5 text-[11px] text-slate-400">
            Klik een rij voor de bevinding + het waargenomen record.
          </p>
        </>
      )}
    </Card>
  );

  // Shared "Wat publiceren" record body (DMARC record + RuaHQ authorization).
  const publishBody = suggest ? (
    <div className="space-y-4 p-4">
      <div>
        <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold text-slate-900">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-info-600 text-[11px] font-bold text-white">
            1
          </span>
          Aanbevolen DMARC-record
        </div>
        {suggest.txt_record ? (
          <RecordFields name={suggest.txt_record_name} value={suggest.txt_record} />
        ) : (
          <p className="text-[12px] text-slate-500">Nog geen aanbeveling beschikbaar.</p>
        )}
        {suggest.instructions_nl && (
          <p className="mt-2 text-[11.5px] leading-relaxed text-slate-600">
            {suggest.instructions_nl}
          </p>
        )}
      </div>
      {suggest.report_authorization_name && (
        <div className="border-t border-info-200 pt-3">
          <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold text-slate-900">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-info-600 text-[11px] font-bold text-white">
              2
            </span>
            RuaHQ-autorisatie <span className="font-normal text-slate-500">(vereist)</span>
          </div>
          <RecordFields
            name={suggest.report_authorization_name}
            value={suggest.report_authorization_value ?? ''}
          />
          {suggest.report_authorization_nl && (
            <p className="mt-2 text-[11.5px] leading-relaxed text-slate-600">
              {suggest.report_authorization_nl}
            </p>
          )}
        </div>
      )}
    </div>
  ) : (
    <div className="p-4 text-[12px] text-slate-500">Nog geen aanbeveling beschikbaar.</div>
  );

  const sendersToShow = showSenders ? signals : signals.slice(0, 5);

  return (
    <div className="max-w-[1600px] space-y-5 p-5">
      <QueryState isLoading={isLoading} isError={isError} onRetry={onRetry}>
        {/* ---- HEADER ---- */}
        <div>
          <nav
            aria-label="Kruimelpad"
            className="mb-3 flex flex-wrap items-center gap-1.5 text-[12px] text-slate-500"
          >
            <Link to="/tenants" className="hover:text-slate-900">
              Tenants
            </Link>
            <ChevronRight size={11} className="text-slate-300" aria-hidden="true" />
            <Link to="/tenants/$slug" params={{ slug }} className="hover:text-slate-900">
              {domain?.tenant_name ?? slug}
            </Link>
            <ChevronRight size={11} className="text-slate-300" aria-hidden="true" />
            <Link to="/tenants/$slug/domains" params={{ slug }} className="hover:text-slate-900">
              Domeinen
            </Link>
            <ChevronRight size={11} className="text-slate-300" aria-hidden="true" />
            <span className="font-medium text-slate-900">{domain?.fqdn}</span>
          </nav>

          <Card className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="font-mono text-[26px] font-bold tracking-tight text-slate-900">
                    {domain?.fqdn}
                  </h1>
                  <StatusBadge status={status} />
                  <PolicyTag policy={currentPolicy} />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-slate-500">
                  <span>
                    <strong className="text-slate-700">Org-domein:</strong>{' '}
                    <span className="font-mono">{domain?.organizational_domain ?? '—'}</span>
                  </span>
                  <span className="text-slate-400">·</span>
                  <span>
                    <strong className="text-slate-700">Gemonitord sinds:</strong>{' '}
                    {domain?.monitored_since ? formatDate(domain.monitored_since) : '—'}
                  </span>
                  <span className="text-slate-400">·</span>
                  <span>
                    <strong className="text-slate-700">Laatste rapport:</strong>{' '}
                    {domain?.last_report_at ? formatDate(domain.last_report_at) : '—'}
                  </span>
                </div>
              </div>
              {/* PRIMARY ACTION — the only action with a real backend (check-dns). */}
              <div className="flex flex-shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => recheckDns.mutate()}
                  disabled={recheckDns.isPending}
                  className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3.5 py-2 text-[12px] font-semibold text-slate-900 hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RefreshCw
                    size={13}
                    className={recheckDns.isPending ? 'animate-spin' : undefined}
                  />
                  {recheckDns.isPending ? 'Bezig…' : 'DNS opnieuw controleren'}
                </button>
              </div>
            </div>
          </Card>
        </div>

        {/* ---- STATUS STRIP ---- */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:col-span-8">
            <KPICard
              label="Volume · 30d"
              value={formatNumber(overview?.total_messages ?? 0)}
              sub={(overview?.total_messages ?? 0) === 0 ? 'wacht op eerste rapport' : 'berichten'}
            />
            <KPICard
              label="DMARC pass"
              value={passText(overview?.pass_rate)}
              accent={passAccent(overview?.pass_rate)}
              sub="SLA-drempel 95 %"
            />
            <KPICard
              label="SPF pass"
              value={passText(overview?.spf_pass_rate)}
              accent={passAccent(overview?.spf_pass_rate)}
              sub="alignment in helper"
            />
            <KPICard
              label="DKIM pass"
              value={passText(overview?.dkim_pass_rate)}
              accent={passAccent(overview?.dkim_pass_rate)}
              sub={`${formatNumber(overview?.unique_source_ips ?? 0)} bronnen`}
            />
          </div>
          <div className="lg:col-span-4">
            <PolicyProgress
              current={currentPolicy}
              desired={desiredPolicy}
              onDesiredChange={(p) => setDesiredPolicy.mutate(p)}
              updating={setDesiredPolicy.isPending}
            />
          </div>
        </div>

        {isOnboarding ? (
          <>
            {/* Onboarding: publiceren is PRIMAIR (col-7), health rechts (col-5). */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <Card className="border-info-200 bg-info-50">
                  <CardHeader
                    title="Wat publiceren"
                    subtitle="twee TXT-records — daarna ontvangt RuaHQ binnen 24–72 u de eerste rapporten"
                    right={
                      <span className="border-info-200 inline-flex items-center gap-1.5 rounded bg-white px-2 py-0.5 text-[11px] font-medium text-info-700 ring-1 ring-info-200">
                        <Shield size={11} />
                        Vereist
                      </span>
                    }
                  />
                  {publishBody}
                </Card>
              </div>
              <div className="lg:col-span-5">{healthCard}</div>
            </div>

            <Card>
              <CardHeader
                title="Verkeer & alignment"
                subtitle="beschikbaar zodra de eerste rapporten binnen zijn"
              />
              <EmptyState
                title="Nog geen verkeer waargenomen"
                body="Publiceer de DNS-records hierboven. Reporters (Google, Microsoft, …) sturen doorgaans binnen 24–72 uur de eerste aggregate rapporten; daarna verschijnen hier de trend en de alignment-analyse."
              />
            </Card>
          </>
        ) : (
          <>
            {/* Monitoring/Mature: trend (col-7) | health (col-5). */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
              <Card className="lg:col-span-7">
                <CardHeader title="Verkeer · 30 dagen" subtitle="dagvolume per uitkomst" />
                <div className="p-4">
                  {timeline.length === 0 ? (
                    <div className="flex h-[220px] items-center justify-center text-[12px] text-slate-500">
                      Nog geen data voor deze periode.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <ComposedChart
                        data={timeline.map((p) => ({
                          day: p.ts ? formatDate(p.ts, 'd MMM') : '',
                          pass: p.pass,
                          quarantine: p.quarantine,
                          reject: p.reject,
                        }))}
                        margin={{ left: 0, right: 8, top: 8, bottom: 0 }}
                      >
                        <CartesianGrid stroke="#e2e8f0" strokeDasharray="0" vertical={false} />
                        <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                        <Tooltip
                          formatter={(v: number) => formatNumber(v)}
                          contentStyle={{ fontSize: 11, borderRadius: 4 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="pass" stackId="x" fill="#16a34a" name="Pass" />
                        <Bar dataKey="quarantine" stackId="x" fill="#d97706" name="Quarantine" />
                        <Bar dataKey="reject" stackId="x" fill="#dc2626" name="Reject" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>
              <div className="lg:col-span-5">{healthCard}</div>
            </div>

            {/* Alignment-helper — suggesties (col-7) | senders (col-5). */}
            <Card>
              <CardHeader
                title="Alignment-helper"
                subtitle="aanbevelingen op basis van gedetecteerde senders"
              />
              {suggestions.length === 0 && signals.length === 0 ? (
                <EmptyState
                  title="Nog geen senders waargenomen"
                  body="De alignment helper analyseert zodra de eerste aggregate rapporten binnen zijn."
                  compact
                />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12">
                  <div className="divide-y divide-slate-100 lg:col-span-7 lg:border-r lg:border-slate-100">
                    {suggestions.map((s, i) => (
                      <div key={i} className="flex items-start gap-3 px-4 py-3">
                        <div className="mt-0.5 flex-shrink-0">
                          {s.severity === 'info' && (
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-50 text-green-700">
                              <Check size={13} />
                            </span>
                          )}
                          {s.severity === 'warn' && (
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-50 text-amber-700">
                              <AlertTriangle size={13} />
                            </span>
                          )}
                          {s.severity === 'critical' && (
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-50 text-red-700">
                              <X size={13} />
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="text-[13px] font-semibold text-slate-900">{s.title}</span>
                          <div className="mt-1 text-[12px] leading-snug text-slate-700">
                            {s.rationale}
                          </div>
                          {s.actions.length > 0 && (
                            <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-[12px] text-slate-600">
                              {s.actions.map((a, j) => (
                                <li key={j}>{a}</li>
                              ))}
                            </ul>
                          )}
                          {s.senders && s.senders.length > 0 && (
                            <div className="mt-1.5 text-[11px] text-slate-500">
                              <span className="text-slate-400">Betreft: </span>
                              {s.senders.slice(0, 3).map((sender, j) => (
                                <span key={sender.source_ip}>
                                  {j > 0 && ', '}
                                  <span className="font-medium text-slate-600">{sender.label}</span>
                                  <span className="ml-1 font-mono text-[10px] text-slate-400">
                                    {sender.source_ip}
                                  </span>
                                </span>
                              ))}
                              {s.senders.length > 3 && (
                                <span className="text-slate-400">
                                  {' '}
                                  +{s.senders.length - 3} andere
                                </span>
                              )}
                            </div>
                          )}
                          {s.docs_url && (
                            <a
                              href={s.docs_url}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 inline-flex items-center gap-1 text-[11px] text-info-700 hover:underline"
                            >
                              Documentatie <ExternalLink size={10} />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="lg:col-span-5">
                    <div className="px-4 py-3">
                      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Gedetecteerde senders ({signals.length})
                      </div>
                      {signals.length === 0 ? (
                        <p className="text-[12px] text-slate-500">Nog geen senders waargenomen.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {sendersToShow.map((sig, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between gap-3 text-[12px]"
                            >
                              <span className="flex min-w-0 items-center gap-1.5 truncate">
                                {sig.status && SENDER_STATUS_BADGE[sig.status] && (
                                  <span
                                    className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${SENDER_STATUS_BADGE[sig.status].className}`}
                                  >
                                    {SENDER_STATUS_BADGE[sig.status].label}
                                  </span>
                                )}
                                <span className="truncate font-medium text-slate-800">
                                  {sig.esp_name ?? sig.reverse_dns ?? sig.source_ip}
                                </span>
                                <span className="flex-shrink-0 font-mono text-[11px] text-slate-400">
                                  {sig.source_ip}
                                </span>
                              </span>
                              <span className="flex-shrink-0 font-mono text-[11px] text-slate-500">
                                SPF{' '}
                                <span
                                  className={
                                    sig.spf_aligned_rate >= 0.9
                                      ? 'text-green-700'
                                      : 'text-amber-700'
                                  }
                                >
                                  {Math.round(sig.spf_aligned_rate * 100)}%
                                </span>
                                <span className="mx-1.5 text-slate-300">·</span>
                                DKIM{' '}
                                <span
                                  className={
                                    sig.dkim_aligned_rate >= 0.9
                                      ? 'text-green-700'
                                      : 'text-amber-700'
                                  }
                                >
                                  {Math.round(sig.dkim_aligned_rate * 100)}%
                                </span>
                              </span>
                            </div>
                          ))}
                          {signals.length > 5 && (
                            <button
                              type="button"
                              onClick={() => setShowSenders((s) => !s)}
                              className="mt-2 text-[11px] font-medium text-info-700 hover:text-info-600"
                            >
                              {showSenders ? 'Toon minder' : `Toon alle ${signals.length} senders`}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </>
        )}

        {/* Wat publiceren — bij gepubliceerde domeinen ingeklapt (tertiair). */}
        {!isOnboarding && (
          <Accordion
            title="Wat publiceren — DNS-records"
            subtitle="aanbevolen DMARC-record + RuaHQ-autorisatie"
            badge={
              <span className="inline-flex items-center gap-1.5 text-[11px] text-green-700">
                <Check size={12} />
                Gepubliceerd
              </span>
            }
          >
            {publishBody}
          </Accordion>
        )}

        {/* ---- TERTIAIR: accordions ---- */}
        <Accordion
          title="Ruwe DNS-snapshot"
          subtitle={
            dns?.fetched_at
              ? `Laatste check ${formatDate(dns.fetched_at, 'd MMM yyyy HH:mm')}`
              : 'laatst opgehaalde DMARC/SPF/DKIM/MX-waarden'
          }
          badge={
            dns?.changed_from_previous ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-700">
                <AlertTriangle size={11} />
                Gewijzigd t.o.v. vorige
              </span>
            ) : dns ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-green-700">
                <Check size={11} />
                Ongewijzigd
              </span>
            ) : undefined
          }
        >
          <div className="space-y-2.5 p-4 text-[12px]">
            {dns ? (
              <>
                <DnsRow label="DMARC" value={dns.dmarc_record ?? '—'} />
                <DnsRow label="SPF" value={dns.spf_record ?? '—'} />
                <DnsRow
                  label="DKIM selectors"
                  mono={false}
                  value={
                    <div className="flex flex-wrap gap-1.5">
                      {Object.keys(dns.dkim_selectors ?? {}).length === 0 && (
                        <span className="italic text-slate-400">—</span>
                      )}
                      {Object.keys(dns.dkim_selectors ?? {}).map((s) => (
                        <span
                          key={s}
                          className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  }
                />
                <DnsRow
                  label="MX"
                  value={
                    dns.mx_records && dns.mx_records.length > 0
                      ? dns.mx_records.map((m) => `${m.priority} ${m.exchange}`).join(' · ')
                      : '—'
                  }
                />
              </>
            ) : (
              <EmptyState
                title="Nog geen DNS-snapshot"
                body="Druk op DNS opnieuw controleren om een eerste snapshot te maken."
                compact
              />
            )}
          </div>
        </Accordion>

        <Accordion
          title="Recente aggregate reports"
          subtitle="laatste 5 XML-rapporten ontvangen voor dit domein"
          badge={
            reports.length > 0 ? (
              <span className="font-mono text-[11px] text-slate-400">{reports.length}</span>
            ) : undefined
          }
        >
          {reports.length === 0 ? (
            <EmptyState
              title="Geen rapporten in deze periode"
              body="Er zijn nog geen aggregate rapporten ontvangen voor dit domein."
              compact
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Reporter
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Ontvangen
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Periode
                    </th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Records
                    </th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {reports.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5 font-medium text-slate-900">{r.reporter_org}</td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-slate-600">
                        {r.received_at ? formatDate(r.received_at) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-500">
                        {r.date_range_begin && r.date_range_end
                          ? `${formatDate(r.date_range_begin)} – ${formatDate(r.date_range_end)}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-900">
                        {formatNumber(r.records_count ?? 0)}
                      </td>
                      <td className="px-3 py-2.5">
                        <Link
                          to="/tenants/$slug/reports/$id"
                          params={{ slug, id: r.id }}
                          className="inline-flex items-center gap-1 text-[11px] text-info-700 hover:text-info-600"
                        >
                          <Eye size={11} />
                          Bekijken
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Accordion>
      </QueryState>
    </div>
  );
}

// ----- sub-components ------------------------------------------------------

function DnsRow({
  label,
  value,
  mono = true,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-start gap-3">
      <span className="pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <div
        className={`${mono ? 'font-mono text-[11.5px]' : 'text-[12px]'} break-all leading-relaxed text-slate-800`}
      >
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'good' | 'warn' | 'fail' | 'onboarding' }) {
  const map = {
    good: { label: 'Stabiel', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-600' },
    warn: { label: 'Aandacht', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-600' },
    fail: { label: 'Kritiek', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-600' },
    onboarding: {
      label: 'Onboarding',
      bg: 'bg-slate-50',
      text: 'text-slate-600',
      dot: 'bg-slate-400',
    },
  } as const;
  const m = map[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs ${m.bg} ${m.text} rounded font-medium`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

function PolicyTag({ policy }: { policy: 'none' | 'quarantine' | 'reject' }) {
  const map = {
    none: { bg: 'bg-slate-100', text: 'text-slate-700' },
    quarantine: { bg: 'bg-amber-50', text: 'text-amber-700' },
    reject: { bg: 'bg-green-50', text: 'text-green-700' },
  } as const;
  const m = map[policy];
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 font-mono text-[11px] font-medium ${m.bg} ${m.text}`}
    >
      p={policy}
    </span>
  );
}

// 3-step DMARC-policy stepper (none → quarantine → reject), marking the
// completed/current step against the desired policy.
function PolicyProgress({
  current,
  desired,
  onDesiredChange,
  updating = false,
}: {
  current: PolicyValue;
  desired: PolicyValue;
  onDesiredChange?: (p: PolicyValue) => void;
  updating?: boolean;
}) {
  const steps: PolicyValue[] = ['none', 'quarantine', 'reject'];
  const labels: Record<PolicyValue, string> = {
    none: 'Monitor',
    quarantine: 'Quarantine',
    reject: 'Reject',
  };
  const curIdx = steps.indexOf(current);
  return (
    <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
      <div className="mb-2.5 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Beleid-progressie
        </span>
        {onDesiredChange ? (
          <label className="flex items-center gap-1 text-[10px] text-slate-400">
            doel:
            <select
              value={desired}
              disabled={updating}
              onChange={(e) => onDesiredChange(e.target.value as PolicyValue)}
              aria-label="Doelbeleid aanpassen"
              className="rounded border border-slate-200 bg-white px-1 py-0.5 font-mono text-[10px] text-slate-700 focus:border-brand-600 focus:outline-none disabled:opacity-50"
            >
              <option value="none">p=none</option>
              <option value="quarantine">p=quarantine</option>
              <option value="reject">p=reject</option>
            </select>
          </label>
        ) : (
          <span className="text-[10px] text-slate-400">
            gewenst: <span className="font-mono text-slate-600">p={desired}</span>
          </span>
        )}
      </div>
      <div className="flex items-center">
        {steps.map((s, i) => {
          const done = i < curIdx;
          const cur = i === curIdx;
          const node = done
            ? 'bg-green-100 border-green-600 text-green-700'
            : cur
              ? 'bg-brand-50 border-brand-600 text-brand-700 ring-1 ring-brand-600'
              : 'bg-white border-slate-300 text-slate-400';
          return (
            <span key={s} className="contents">
              <span className="flex flex-shrink-0 flex-col items-center gap-1">
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-bold ${node}`}
                >
                  {done ? <Check size={13} /> : i + 1}
                </span>
                <span
                  className={`text-[10px] font-medium ${cur ? 'text-brand-700' : 'text-slate-500'}`}
                >
                  {labels[s]}
                </span>
              </span>
              {i < steps.length - 1 && (
                <span
                  className={`mx-1 mb-4 h-px flex-1 ${i < curIdx ? 'bg-green-600' : 'bg-slate-300'}`}
                  aria-hidden="true"
                />
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// Overall DNS-health verdict badge (text + colour, never colour-only).
function HealthOverallBadge({ overall }: { overall: 'ok' | 'warn' | 'fail' }) {
  const map = {
    ok: { label: 'Gezond', cls: 'bg-green-50 text-green-700' },
    warn: { label: 'Aandacht vereist', cls: 'bg-amber-50 text-amber-700' },
    fail: { label: 'Probleem', cls: 'bg-red-50 text-red-700' },
  } as const;
  const m = map[overall];
  return <span className={`rounded px-2 py-0.5 font-medium ${m.cls}`}>{m.label}</span>;
}

// Compact, expandable health row: badge + 1-line summary; expanding reveals
// the NL detail + the observed record (via DnsCheckRecord).
function HealthRow({ check, dns }: { check: DnsHealthCheck; dns?: DnsSnapshot }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50"
      >
        <ChevronRight
          size={13}
          className={`flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
        <span className="w-20 flex-shrink-0 font-mono text-[11px] uppercase text-slate-700">
          {check.field}
          {check.selector ? <span className="text-slate-400"> ({check.selector})</span> : null}
        </span>
        <span className="flex-shrink-0">
          <DnsHealthStatusBadge status={check.status} />
        </span>
        <span className="min-w-0 flex-1 truncate text-[12px] text-slate-700">{check.summary}</span>
      </button>
      {open && (
        <div className="bg-slate-50 px-4 pb-3 pl-[52px] pt-1">
          <p className="mb-2 text-[12px] leading-snug text-slate-600">{check.detail_nl}</p>
          <div className="break-all rounded bg-white px-2.5 py-1.5 font-mono text-[11px] leading-relaxed text-slate-700 ring-1 ring-slate-200">
            <DnsCheckRecord check={check} dns={dns} />
          </div>
        </div>
      )}
    </div>
  );
}

// Collapsible Card with an accessible button header (rotating chevron).
function Accordion({
  title,
  subtitle,
  children,
  defaultOpen = false,
  badge,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
      >
        <div className="flex items-center gap-2.5">
          <ChevronRight
            size={14}
            className={`text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
          <div>
            <h3 className="text-[13px] font-semibold text-slate-900">{title}</h3>
            {subtitle && <p className="mt-0.5 text-[11px] text-slate-500">{subtitle}</p>}
          </div>
        </div>
        {badge}
      </button>
      {open && <div className="border-t border-slate-200">{children}</div>}
    </Card>
  );
}

function DnsHealthStatusBadge({ status }: { status: DnsHealthStatus }) {
  const map: Record<DnsHealthStatus, { label: string; cls: string }> = {
    ok: { label: 'OK', cls: 'bg-green-50 text-green-700' },
    warn: { label: 'Warn', cls: 'bg-amber-50 text-amber-700' },
    fail: { label: 'Fail', cls: 'bg-red-50 text-red-700' },
    missing: { label: 'Ontbreekt', cls: 'bg-slate-100 text-slate-700' },
  };
  const m = map[status];
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
}

const NO_RECORD = '— geen record —';

// Renders the actual observed DNS record content (from the latest snapshot)
// for a given health-check row, so the operator sees not just "OK/Fail" but
// what is actually published in DNS.
function DnsCheckRecord({ check, dns }: { check: DnsHealthCheck; dns?: DnsSnapshot }) {
  const mono = 'font-mono text-[11px] leading-relaxed break-all text-slate-700';
  const empty = <span className="italic text-slate-400">{NO_RECORD}</span>;

  if (!dns) return empty;

  if (check.field === 'dmarc') {
    return dns.dmarc_record ? <span className={mono}>{dns.dmarc_record}</span> : empty;
  }

  if (check.field === 'spf') {
    return dns.spf_record ? <span className={mono}>{dns.spf_record}</span> : empty;
  }

  if (check.field === 'mx') {
    const mx = dns.mx_records ?? [];
    if (mx.length === 0) return empty;
    return (
      <div className={mono}>
        {mx.map((m, i) => (
          <div key={`${m.priority}-${m.exchange}-${i}`}>
            {m.priority} {m.exchange}
          </div>
        ))}
      </div>
    );
  }

  // dkim — dkim_selectors is a map selector → key. When the check targets a
  // specific selector, show that one's key; otherwise list all selectors.
  const selectors = dns.dkim_selectors ?? {};
  const entries = check.selector
    ? Object.entries(selectors).filter(([sel]) => sel === check.selector)
    : Object.entries(selectors);
  if (entries.length === 0) return empty;
  return (
    <div className="space-y-1">
      {entries.map(([sel, key]) => (
        <div key={sel} className={mono}>
          <span className="text-slate-500">{sel}:</span> {key || NO_RECORD}
        </div>
      ))}
    </div>
  );
}
